import { useState, useEffect, useCallback, useMemo, memo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { 
  Users, 
  UserCheck, 
  UserMinus, 
  UserX, 
  Search, 
  RefreshCw,
  Calendar,
  Palmtree,
  ShieldCheck,
  ShieldOff,
  LogOut,
  Settings,
  Activity,
  Download,
  Loader2,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getInitials = (name) => {
  if (!name) return "??";
  const parts = name.split(" ");
  return parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase() : name.substring(0, 2).toUpperCase();
};

const getAvatarColor = (name) => {
  const colors = ["bg-emerald-600", "bg-blue-600", "bg-purple-600", "bg-pink-600", "bg-amber-600", "bg-teal-600", "bg-indigo-600", "bg-rose-600"];
  return name ? colors[name.charCodeAt(0) % colors.length] : colors[0];
};

const getGlowClass = (status) => {
  const map = { checked_in: 'status-glow-in', checked_out: 'status-glow-out', attend: 'status-glow-in', absent: 'status-glow-absent', on_leave: 'status-glow-leave', exempted: 'status-glow-exempt' };
  return map[status] || '';
};

const SummaryCard = memo(({ icon: Icon, label, value, colorClass, onClick, isActive }) => (
  <div 
    data-testid={`summary-card-${label.toLowerCase().replace(/\s/g, '-')}`}
    className={`summary-card flex items-center justify-between cursor-pointer transition-all ${isActive ? 'ring-2 ring-white/30' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-center gap-2">
      <Icon className={`w-4 h-4 ${colorClass || 'text-zinc-500'}`} />
      <span className="summary-card-label">{label}</span>
    </div>
    <div className={`summary-card-value ${colorClass || 'text-white'}`}>{value}</div>
  </div>
));

const EmployeeCard = memo(({ employee, onToggleExemption, isAdmin }) => {
  const statusLabels = { checked_in: 'IN', checked_out: 'OUT', attend: 'ATTEND', absent: 'ABSENT', on_leave: 'LEAVE', exempted: 'EXEMPT', unknown: 'NOT IN' };
  const status = employee.attendance_status || 'unknown';
  const displayName = employee.name || 'Unknown';
  const hasAvatar = employee.avatar?.startsWith('http');

  return (
    <div data-testid={`employee-card-${employee.code}`} className={`employee-card relative flex flex-col gap-1 ${getGlowClass(status)}`} style={{ padding: '0.4rem 0.5rem' }}>
      <span className={`status-badge absolute top-1 right-1 text-[9px] px-1.5 py-0.5 ${status.replace('_', '-')}`}>{statusLabels[status]}</span>
      <div className="flex items-center gap-1.5 pr-10">
        {hasAvatar ? (
          <img src={employee.avatar} alt="" loading="lazy" decoding="async" className="w-6 h-6 rounded-full object-cover border border-zinc-700 flex-shrink-0"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
        ) : null}
        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white font-medium text-[9px] flex-shrink-0 ${getAvatarColor(displayName)}`}
          style={{ display: hasAvatar ? 'none' : 'flex' }}>{getInitials(displayName)}</div>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-white font-medium text-xs truncate">{displayName}</span>
          <div className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500 whitespace-nowrap">
            <span>#{employee.code}</span>
            {!employee.is_exempted && employee.check_in_time && <span className="text-emerald-400/70">{employee.check_in_time}{employee.check_out_time ? ` - ${employee.check_out_time}` : ''}</span>}
          </div>
        </div>
      </div>

      {isAdmin && (
        <div className="flex justify-end pt-1 border-t border-zinc-800/50">
          <button data-testid={`exempt-btn-${employee.code}`} onClick={() => onToggleExemption(employee)}
            className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded transition-colors ${employee.is_exempted ? 'text-cyan-400 hover:bg-cyan-900/30' : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'}`}>
            {employee.is_exempted ? <><ShieldOff className="w-3 h-3" />Remove</> : <><ShieldCheck className="w-3 h-3" />Exempt</>}
          </button>
        </div>
      )}
    </div>
  );
});

const AttendanceDashboard = () => {
  const { user, token, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [stats, setStats] = useState({ total: 0, checked_in: 0, checked_out: 0, absent: 0, on_leave: 0, exempted: 0, attend: 0 });
  
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().split('T')[0];
  
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [isToday, setIsToday] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showExemptionDialog, setShowExemptionDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [exemptionReason, setExemptionReason] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimerRef = useRef(null);

  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(val), 200);
  }, []);

  const fetchDashboardData = useCallback(async (forceRefresh = false) => {
    try {
      setRefreshing(true);
      setError(null);
      const response = await axios.get(`${API}/jisr/dashboard`, { 
        params: { date: selectedDate, force_refresh: forceRefresh },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        const data = response.data.data;
        setEmployees(data.employees || []);
        setDepartments(data.departments || []);
        setStats(data.stats || { total: 0, checked_in: 0, checked_out: 0, absent: 0, on_leave: 0, exempted: 0, attend: 0 });
        setIsToday(data.is_today);
        setLastUpdate(new Date());
        if (data.is_today && filter === 'absent') setFilter('all');
        if (!data.is_today && (filter === 'checked_in' || filter === 'checked_out' || filter === 'not_attend')) setFilter('all');
      } else {
        setError(response.data.message || 'Failed to fetch data');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to connect');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedDate, filter, token]);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const endpoint = format === 'pdf' ? 'export-pdf' : 'export';
      const response = await axios.get(`${API}/jisr/${endpoint}`, {
        params: { date: selectedDate },
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_${selectedDate}.${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error('Export failed:', err);
    }
    setExporting(null);
  };

  const handleStatClick = (statType) => {
    if (statType === 'absent' && isToday) return;
    const map = { total: 'all', checked_in: 'checked_in', checked_out: 'checked_out', absent: 'absent', on_leave: 'on_leave', exempted: 'exempted', not_attend: 'not_attend', attend: 'attend' };
    setFilter(map[statType] || 'all');
  };

  const handleToggleExemption = async (employee) => {
    if (!isAdmin) return;
    
    if (employee.is_exempted) {
      try { 
        await axios.delete(`${API}/exemptions/${employee.code}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }); 
        fetchDashboardData(true); 
      } catch (err) { console.error(err); }
    } else {
      setSelectedEmployee(employee);
      setExemptionReason('');
      setShowExemptionDialog(true);
    }
  };

  const handleAddExemption = async () => {
    if (!selectedEmployee || !isAdmin) return;
    try {
      await axios.post(`${API}/exemptions`, 
        { employee_code: selectedEmployee.code, employee_name: selectedEmployee.name, reason: exemptionReason || 'Exempted' },
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      setShowExemptionDialog(false);
      setSelectedEmployee(null);
      fetchDashboardData(true);
    } catch (err) { console.error(err); }
  };

  // Total attending = attend + checked_in + checked_out
  const totalAttending = stats.attend + stats.checked_in + stats.checked_out;
  // Not in = total - attending - exempted - on_leave
  const notInCount = stats.total - totalAttending - stats.exempted - stats.on_leave;

  const filteredEmployees = useMemo(() => employees.filter(emp => {
    const s = emp.attendance_status;
    if (s === 'exempted' && filter !== 'exempted') return false;
    if (filter === 'all') {
      if (s === 'on_leave' || s === 'unknown' || s === 'absent') return false;
    }
    if (filter === 'not_attend') return s === 'unknown' || s === 'absent';
    if (filter === 'attend') return s === 'attend' || s === 'checked_in' || s === 'checked_out';
    if (filter !== 'all' && filter !== 'exempted' && filter !== 'not_attend' && filter !== 'attend' && s !== filter) return false;
    if (filter === 'exempted' && s !== 'exempted') return false;
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      if (!(emp.name || '').toLowerCase().includes(q) && !String(emp.code).includes(q)) return false;
    }
    return true;
  }), [employees, filter, debouncedSearch]);

  const formatTime = (date) => date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><RefreshCw className="w-6 h-6 text-zinc-500 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4">
        <header className="flex justify-between items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <Calendar className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base sm:text-lg font-semibold text-white">Attendance</h1>
                <span className="text-xs font-mono text-zinc-400">Last update {formatTime(lastUpdate)}</span>
              </div>
              <p className="text-[10px] text-zinc-500">{isToday ? 'Today' : 'Yesterday'} • {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <>
              <Button variant="outline" size="sm" onClick={() => navigate('/admin/activity')}
                className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800 px-2 h-7" data-testid="activity-btn">
                <Activity className="w-3.5 h-3.5" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/admin')}
                className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800 px-2 h-7" data-testid="admin-btn">
                <Settings className="w-3.5 h-3.5" />
              </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={() => handleExport('xlsx')} disabled={exporting === 'xlsx'}
              className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-7 px-2 text-[11px]" data-testid="export-excel-btn">
              {exporting === 'xlsx' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Download className="w-3 h-3 mr-1" />Excel</>}
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} disabled={exporting === 'pdf'}
              className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-7 px-2 text-[11px]" data-testid="export-pdf-btn">
              {exporting === 'pdf' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Download className="w-3 h-3 mr-1" />PDF</>}
            </Button>
            <Button variant="outline" size="sm" onClick={() => fetchDashboardData(true)} disabled={refreshing}
              className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800 px-2 h-7">
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="outline" size="sm" onClick={logout}
              className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800 px-2 h-7">
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        </header>

        {error && <div className="mb-4 p-3 bg-red-900/20 border border-red-800/50 rounded-lg text-red-400 text-xs">{error}</div>}

        <div className={`grid grid-cols-3 ${isToday ? 'sm:grid-cols-6' : 'sm:grid-cols-4'} gap-2 sm:gap-3 mb-4`}>
          <SummaryCard icon={UserCheck} label="ATTEND" value={totalAttending} colorClass="text-blue-400" onClick={() => handleStatClick('attend')} isActive={filter === 'all' || filter === 'attend'} />
          {isToday && (
            <>
              <SummaryCard icon={UserCheck} label="IN" value={stats.checked_in} colorClass="text-emerald-400" onClick={() => handleStatClick('checked_in')} isActive={filter === 'checked_in'} />
              <SummaryCard icon={UserMinus} label="OUT" value={stats.checked_out} colorClass="text-amber-400" onClick={() => handleStatClick('checked_out')} isActive={filter === 'checked_out'} />
              <SummaryCard icon={UserX} label="NOT IN" value={notInCount} colorClass="text-red-400" onClick={() => handleStatClick('not_attend')} isActive={filter === 'not_attend'} />
            </>
          )}
          {!isToday && <SummaryCard icon={UserX} label="ABSENT" value={stats.absent} colorClass="text-red-400" onClick={() => handleStatClick('not_attend')} isActive={filter === 'not_attend'} />}
          <SummaryCard icon={Palmtree} label="LEAVE" value={stats.on_leave} colorClass="text-purple-400" onClick={() => handleStatClick('on_leave')} isActive={filter === 'on_leave'} />
          <SummaryCard icon={ShieldCheck} label="EXEMPT" value={stats.exempted} colorClass="text-cyan-400" onClick={() => handleStatClick('exempted')} isActive={filter === 'exempted'} />
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="flex gap-1.5">
            <Button size="sm" onClick={() => setSelectedDate(todayStr)} className={`h-7 px-3 text-xs ${isToday ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'}`}>Today</Button>
            <Button size="sm" onClick={() => setSelectedDate(yesterdayStr)} className={`h-7 px-3 text-xs ${!isToday ? 'bg-amber-600 hover:bg-amber-700' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'}`}>Yesterday</Button>
          </div>
          <div className="relative flex-1 min-w-[120px] max-w-[200px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
            <Input type="text" placeholder="Search..." value={searchQuery} onChange={handleSearchChange} className="pl-7 h-7 text-xs bg-zinc-900 border-zinc-800 text-zinc-300" />
          </div>
        </div>

        {filter !== 'all' && (
          <div className="mb-3 flex items-center gap-2 text-xs">
            <span className="px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 capitalize">{filter.replace('_', ' ')}</span>
            <button onClick={() => setFilter('all')} className="text-zinc-500 hover:text-zinc-300 underline">Clear</button>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2 sm:gap-3">
          {filteredEmployees.length > 0 ? filteredEmployees.map(emp => <EmployeeCard key={emp.id || emp.code} employee={emp} onToggleExemption={handleToggleExemption} isAdmin={isAdmin} />)
            : <div className="col-span-full py-12 text-center text-zinc-500"><Users className="w-8 h-8 mx-auto mb-2 opacity-50" /><p className="text-sm">No employees found</p></div>}
        </div>
      </div>

      <Dialog open={showExemptionDialog} onOpenChange={setShowExemptionDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><ShieldCheck className="w-4 h-4 text-cyan-400" />Exempt Employee</DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">{selectedEmployee?.name}</DialogDescription>
          </DialogHeader>
          <Input type="text" placeholder="Reason (optional)" value={exemptionReason} onChange={(e) => setExemptionReason(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white text-sm" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowExemptionDialog(false)} className="bg-transparent border-zinc-700 text-zinc-300 text-sm h-8">Cancel</Button>
            <Button onClick={handleAddExemption} className="bg-cyan-600 hover:bg-cyan-700 text-sm h-8">Exempt</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendanceDashboard;
